/**
 * UsersPage — Gestion des utilisateurs Assistia (S5.4-12)
 *
 * Fonctionnalités :
 *   - Liste des utilisateurs actifs (tableau trié par email)
 *   - Création d'un utilisateur (modale)
 *   - Modification du rôle (select inline)
 *   - Grant / Revoke Premium
 *   - Soft-delete (désactivation)
 *
 * Contrainte : zéro boucle for/while — style fonctionnel strict.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, ShieldCheck, ShieldOff, Trash2, UserCog, Lock, Unlock } from 'lucide-react';
import { useSDK } from '../context/SDKContext.tsx';
import type { AdminUser, UserRole, DataClearance } from '@assistia/sdk';
import { CLEARANCE_LABEL } from '../utils/clearance.ts';

// ── Constantes ────────────────────────────────────────────────────────────────

const ROLES: UserRole[] = ['admin', 'manager', 'user', 'auditor'];
const CLEARANCES: DataClearance[] = ['C1', 'C2', 'C3', 'C4'];

const ROLE_COLORS: Record<UserRole, string> = {
  root:    'bg-red-500/15 text-red-600 font-bold',
  admin:   'bg-primary/15 text-primary',
  manager: 'bg-yellow-500/15 text-yellow-600',
  user:    'bg-muted text-muted-foreground',
  auditor: 'bg-blue-500/15 text-blue-600',
};

// ── Composants internes ───────────────────────────────────────────────────────

function RoleBadge({ role }: { role: UserRole }): React.ReactElement {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[role]}`}>
      {role}
    </span>
  );
}

interface CreateModalProps {
  onClose: () => void;
  onCreated: (user: AdminUser) => void;
}

function CreateUserModal({ onClose, onCreated }: CreateModalProps): React.ReactElement {
  const { sdk } = useSDK();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [clearance, setClearance] = useState<DataClearance>('C2');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await sdk.admin.createUser({ email, password, role, clearance });
      onCreated(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Créer un utilisateur</h2>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded p-3">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
              placeholder="utilisateur@domaine.fr"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
              placeholder="≥ 8 caractères"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Rôle</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
            >
              {ROLES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Habilitation (clearance)</label>
            <select
              value={clearance}
              onChange={e => setClearance(e.target.value as DataClearance)}
              className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
            >
              {CLEARANCES.map(c => (
                <option key={c} value={c}>{CLEARANCE_LABEL[c]}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded border border-border hover:bg-muted transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
              {loading ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface TotpModalProps {
  user: AdminUser;
  onClose: () => void;
  onUpdated: () => void;
}

function TotpModal({ user, onClose, onUpdated }: TotpModalProps): React.ReactElement {
  const { sdk } = useSDK();
  const [step, setStep] = useState<'init' | 'setup' | 'recovery'>('init');
  const [setupInfo, setSetupInfo] = useState<{ otpauth_uri: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user.totp_enabled) {
      void handleSetup();
    }
  }, [user.totp_enabled]);

  const handleSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await sdk.admin.setupUserTotp(user.id);
      setSetupInfo(data);
      setStep('setup');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await sdk.admin.enableUserTotp(user.id, code);
      setRecoveryCodes(data.recovery_codes);
      setStep('recovery');
      onUpdated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          Configuration TOTP pour {user.email}
        </h2>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded p-3">{error}</div>
        )}

        {step === 'setup' && setupInfo && (
          <div className="space-y-4 flex flex-col items-center">
            <p className="text-sm text-center">
              1. Demandez à l'utilisateur de scanner ce QR code avec son application authenticator (Google Authenticator, Authy, etc).
            </p>
            <div className="bg-white p-2 rounded-lg">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setupInfo.otpauth_uri)}`}
                alt="QR Code TOTP"
                className="w-44 h-44"
              />
            </div>
            <p className="text-xs text-muted-foreground font-mono bg-muted p-1 rounded">
              {setupInfo.secret}
            </p>
            <form onSubmit={handleEnable} className="w-full space-y-3 mt-4 border-t border-border pt-4">
              <p className="text-sm text-center">
                2. Saisissez le premier code généré par l'application pour activer le TOTP :
              </p>
              <input
                type="text"
                required
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full text-center tracking-widest text-lg font-mono px-3 py-2 rounded border border-input bg-background"
              />
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm rounded border border-border hover:bg-muted transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={loading || code.length !== 6}
                  className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
                  {loading ? 'Activation…' : 'Activer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'recovery' && (
          <div className="space-y-4">
            <div className="bg-green-500/15 text-green-600 p-3 rounded text-sm text-center font-medium">
              ✅ TOTP activé avec succès.
            </div>
            <p className="text-sm text-muted-foreground">
              Transmettez ces codes de récupération à l'utilisateur. Ils ne seront affichés qu'une seule fois.
            </p>
            <div className="grid grid-cols-2 gap-2 bg-muted p-4 rounded-lg font-mono text-sm">
              {recoveryCodes.map(c => (
                <div key={c} className="text-center">{c}</div>
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export default function UsersPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [totpUser, setTotpUser] = useState<AdminUser | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await sdk.admin.listUsers();
      setUsers(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => { void loadUsers(); }, [loadUsers]);

  const handleRoleChange = async (user: AdminUser, role: UserRole) => {
    setActionId(user.id);
    try {
      // Le backend met à jour rôle + clearance ensemble : on conserve la clearance actuelle.
      const { data } = await sdk.admin.updateUserRole(user.id, role, user.clearance);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: data.role, clearance: data.clearance } : u));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionId(null);
    }
  };

  const handleClearanceChange = async (user: AdminUser, clearance: DataClearance) => {
    setActionId(user.id);
    try {
      const { data } = await sdk.admin.updateUserRole(user.id, user.role, clearance);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: data.role, clearance: data.clearance } : u));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionId(null);
    }
  };

  const handleTogglePremium = async (user: AdminUser) => {
    setActionId(user.id);
    try {
      if (user.is_premium) {
        await sdk.admin.revokePremium(user.id);
      } else {
        await sdk.admin.grantPremium(user.id);
      }
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_premium: !u.is_premium } : u));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm('Désactiver cet utilisateur ?')) return;
    setActionId(userId);
    try {
      await sdk.admin.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionId(null);
    }
  };

  const handleDisableTotp = async (user: AdminUser) => {
    if (user.role === 'root') {
      alert("Impossible de désactiver le TOTP pour le compte ROOT.");
      return;
    }
    if (!confirm(`Désactiver le TOTP pour ${user.email} ?`)) return;
    setActionId(user.id);
    try {
      await sdk.admin.disableUserTotp(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, totp_enabled: false } : u));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6">
      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={user => { setUsers(prev => [...prev, user]); setShowCreate(false); }}
        />
      )}

      {totpUser && (
        <TotpModal
          user={totpUser}
          onClose={() => setTotpUser(null)}
          onUpdated={() => {
            setUsers(prev => prev.map(u => u.id === totpUser.id ? { ...u, totp_enabled: true } : u));
            // Ne pas fermer immédiatement pour laisser l'admin copier les codes
          }}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {users.length} compte{users.length !== 1 ? 's' : ''} actif{users.length !== 1 ? 's' : ''}
            {isSimulated && <span className="ml-2 text-orange-500">(simulation)</span>}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          Nouvel utilisateur
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : users.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Aucun utilisateur actif.
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Rôle</th>
                <th className="px-4 py-3 font-medium">Habilitation</th>
                <th className="px-4 py-3 font-medium">Premium</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground">{user.email}</td>
                  <td className="px-4 py-3">
                    <select
                      value={user.role}
                      disabled={actionId === user.id}
                      onChange={e => handleRoleChange(user, e.target.value as UserRole)}
                      className="px-2 py-1 rounded border border-input bg-background text-xs disabled:opacity-50"
                    >
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={user.clearance ?? 'C2'}
                      disabled={actionId === user.id}
                      onChange={e => handleClearanceChange(user, e.target.value as DataClearance)}
                      className="px-2 py-1 rounded border border-input bg-background text-xs disabled:opacity-50"
                      title={CLEARANCE_LABEL[user.clearance ?? 'C2']}
                    >
                      {CLEARANCES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleTogglePremium(user)}
                      disabled={actionId === user.id}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                        user.is_premium
                          ? 'bg-primary/10 text-primary hover:bg-destructive/10 hover:text-destructive'
                          : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
                      }`}
                    >
                      {user.is_premium
                        ? <><ShieldCheck size={12} /> Premium</>
                        : <><ShieldOff size={12} /> Standard</>
                      }
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {user.totp_enabled ? (
                      <button
                        onClick={() => handleDisableTotp(user)}
                        disabled={actionId === user.id || user.role === 'root'}
                        title={user.role === 'root' ? "TOTP obligatoire pour ROOT" : "Désactiver le TOTP"}
                        className="p-1.5 rounded text-green-600 hover:bg-green-600/10 disabled:opacity-50 transition-colors"
                      >
                        <Lock size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() => setTotpUser(user)}
                        disabled={actionId === user.id}
                        title="Activer le TOTP"
                        className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
                      >
                        <Unlock size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(user.id)}
                      disabled={actionId === user.id}
                      title="Désactiver l'utilisateur"
                      className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-50 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        <UserCog size={12} className="inline mr-1" />
        La désactivation est réversible — les données sont conservées.
      </p>
    </div>
  );
}
